import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { SCORE_INFO, scoreTone, DECISOES_OVERRIDE, DECISOES_OVERRIDE_LABELS } from '@/pages/painel/lib/inteligencia';

interface ScoreComponente {
  key: string;
  label: string;
  max: number;
  pontos: number;
  evidencia: string | null;
}

interface Score {
  score: number;
  max: number;
  componentes: ScoreComponente[];
  dados_insuficientes: boolean;
}

interface Scores {
  lead: Score;
  reativacao: Score;
  arquiteto: Score;
  fornecedor: Score;
}

interface Override {
  id: number;
  entidade_id: number;
  decisao_manual: string;
  motivo_decisao_manual: string | null;
  data_decisao_manual: string | null;
}

export default function InteligenciaRelacionamento({ empresaId, password }: { empresaId: number; password: string }) {
  const [scores, setScores] = useState<Scores | null>(null);
  const [override, setOverride] = useState<Override | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [decisao, setDecisao] = useState('');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [scoreRes, overrideRes] = await Promise.all([
        supabase.functions.invoke('inteligencia-admin', { body: { action: 'score_empresa', password, empresa_id: empresaId } }),
        supabase.functions.invoke('inteligencia-admin', { body: { action: 'override_listar', password, entidade_tipo: 'empresa' } }),
      ]);

      const parsedScore = scoreRes.data as { scores?: Scores; error?: string };
      if (parsedScore?.error) throw new Error(parsedScore.error);
      setScores(parsedScore?.scores ?? null);

      const parsedOverride = overrideRes.data as { overrides?: Override[]; error?: string };
      if (parsedOverride?.error) throw new Error(parsedOverride.error);
      const empOverride = (parsedOverride?.overrides ?? []).find((o) => Number(o.entidade_id) === empresaId) ?? null;
      // O override da empresa pode vir em overrides[]; localiza por entidade (o backend retorna todos).
      setOverride(empOverride ?? null);
      if (empOverride) {
        setDecisao(empOverride.decisao_manual);
        setMotivo(empOverride.motivo_decisao_manual ?? '');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar inteligência.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (empresaId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaId, password]);

  const saveOverride = async () => {
    if (!decisao) return;
    setSaving(true);
    try {
      await supabase.functions.invoke('inteligencia-admin', {
        body: {
          action: 'override_salvar',
          password,
          entidade_tipo: 'empresa',
          entidade_id: empresaId,
          decisao_manual: decisao,
          motivo_decisao_manual: motivo,
        },
      });
      await load();
    } catch {
      // silencioso
    } finally {
      setSaving(false);
    }
  };

  const removeOverride = async () => {
    setSaving(true);
    try {
      await supabase.functions.invoke('inteligencia-admin', {
        body: { action: 'override_remover', password, entidade_tipo: 'empresa', entidade_id: empresaId },
      });
      setDecisao('');
      setMotivo('');
      await load();
    } catch {
      // silencioso
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <i className="ri-brain-line text-[#E8610A]"></i>
        <h3 className="text-sm font-bold text-gray-900">Inteligência de Relacionamento</h3>
      </div>
      <p className="text-xs text-gray-500 mb-5">
        Scores transparentes (nunca caixa-preta). Dados factuais são separados de recomendações — clique em "Por quê?" para ver os fatores.
      </p>

      {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-4">{error}</div>}

      {loading && !scores ? (
        <div className="flex justify-center py-10"><div className="w-7 h-7 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" /></div>
      ) : scores ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {Object.entries(scores).map(([key, score]) => {
            const info = SCORE_INFO[key] ?? { label: key, icon: 'ri-bar-chart-line', desc: '' };
            const tone = scoreTone(score.score);
            return (
              <div key={key} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 flex items-center justify-center bg-gray-100 text-gray-600 rounded-lg shrink-0">
                      <i className={`${info.icon} text-base`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">{info.label}</p>
                      <p className="text-[11px] text-gray-400 truncate">{info.desc}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-2xl font-bold text-gray-900">{score.score}</p>
                    <span className={`inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 border ${tone.tone}`}>{tone.label}</span>
                  </div>
                </div>

                {score.dados_insuficientes && (
                  <p className="text-[11px] text-gray-400 mt-2">Dados ainda insuficientes — score incompleto, sem evidências inventadas.</p>
                )}

                <button
                  onClick={() => setExpanded(expanded === key ? null : key)}
                  className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-question-line"></i>
                  Por quê?
                </button>

                {expanded === key && (
                  <div className="mt-3 space-y-1.5">
                    {score.componentes.map((c) => (
                      <div key={c.key} className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-xs text-gray-700">{c.label}</p>
                          {c.evidencia ? (
                            <p className="text-[11px] text-green-700">{c.evidencia}</p>
                          ) : (
                            <p className="text-[11px] text-gray-300">sem dado</p>
                          )}
                        </div>
                        <span className={`text-xs font-semibold whitespace-nowrap ${c.pontos > 0 ? 'text-gray-900' : 'text-gray-300'}`}>
                          +{c.pontos}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : null}

      {/* Override manual */}
      <div className="mt-6 pt-5 border-t border-gray-100">
        <div className="flex items-center gap-2 mb-1">
          <i className="ri-shield-check-line text-[#E8610A]"></i>
          <h4 className="text-sm font-bold text-gray-900">Override manual</h4>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Uma decisão sua prevalece sobre IA, score e filas automáticas. Ex.: "Não prospectar" nunca será contrariado por score = 100.
        </p>

        {override && (
          <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-amber-800">
                Override ativo: {DECISOES_OVERRIDE_LABELS[override.decisao_manual] ?? override.decisao_manual}
              </span>
              {override.motivo_decisao_manual && (
                <span className="text-xs text-amber-700">— {override.motivo_decisao_manual}</span>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={decisao}
            onChange={(e) => setDecisao(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none sm:min-w-[220px]"
          >
            <option value="">— Sem override —</option>
            {DECISOES_OVERRIDE.map((d) => (
              <option key={d.key} value={d.key}>{d.label}</option>
            ))}
          </select>
          <input
            type="text"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Motivo (opcional)"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:border-[#E8610A]"
          />
          <button
            onClick={saveOverride}
            disabled={saving || !decisao}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg px-4 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
            Salvar override
          </button>
          {override && (
            <button
              onClick={removeOverride}
              disabled={saving}
              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600 rounded-lg px-3 py-2 cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              Remover
            </button>
          )}
        </div>
      </div>
    </div>
  );
}